import { cloneDeep } from 'lodash';
import { deviceType } from 'react-device-detect';

import { User } from '@utils/backend-api/currentUser';
import { SubscribeUserResponse } from '@utils/backend-api/subscribe';
import { LEAD_CREATED_EVENT, LeadDetails } from '@utils/backend-api/user';
import { transformToSnakeCase } from '@utils/formatting';

import { analyticsIdentify } from '../application/javascripts/analytics_util';

export enum Event {
  Click = 'click',
  Impression = 'impression',
  Lead = 'lead',
  Subscribe = 'subscribe',
  Typing = 'typing',
}

export enum ContentType {
  Article = 'article',
  Briefing = 'briefing',
  Comment = 'comment',
  ForumPost = 'forum_post',
  Newsletter = 'newsletter',
  OrgChart = 'org_chart',
  SubscriberCouncil = 'subscriber_council',
}

export enum Page {
  Article = 'article',
  Briefing = 'briefing',
  Homepage = 'homepage',
  MultiOffersSubscribe = 'multi_offers_subscribe',
  Newsletter = 'newsletter',
  OrgChart = 'org_chart',
  SingleOfferSubscribe = 'single_offer_subscribe',
}

export enum ClickTarget {
  AnchorLink = 'anchor_link',
  AppleSignIn = 'apple_sign_in',
  ArticleImage = 'article_image',
  ArticlePickupTitle = 'article_pickup_title',
  Author = 'author',
  BrandPartnershipAd = 'brand_partnership_ad',
  BriefingSource = 'briefing_source',
  Close = 'close',
  CommentLike = 'comment_like',
  CommentMessage = 'comment_message',
  CommentUnlike = 'comment_unlike',
  CommentPost = 'comment_post',
  CommentReply = 'comment_reply',
  CommentReport = 'comment_report',
  CommunityMenuItem = 'community_menu_item',
  Continue = 'continue',
  CopyShareLink = 'copy_share_link',
  CorporateLeadCapture = 'corporate_lead_capture',
  CorporateSubscriptionAd = 'corporate_subscription_ad',
  Directory = 'directory',
  DropDown = 'drop_down',
  FeaturedArticleHeadline = 'featured_article_headline',
  FeaturedArticleImage = 'featured_article_image',
  FeedBriefing = 'feed_briefing',
  Finance = 'finance',
  FinePrintNavItem = 'fine_print_nav_item',
  Follow = 'follow',
  ForumHeadline = 'forum_headline',
  GetStarted = 'get_started',
  GoogleSignIn = 'google_sign_in',
  Headline = 'headline',
  Highlight = 'highlight',
  JumpToComments = 'jump_to_comments',
  LeadCapture = 'lead_capture',
  LearnMore = 'learn_more',
  Logo = 'logo',
  MailTo = 'mail_to',
  MoreMenuItem = 'more_menu_item',
  OrgCharts = 'org_charts',
  Offer = 'offer',
  ProfileAndArchive = 'profile_and_archive',
  ProMenuItem = 'pro_menu_item',
  RecommendedArticle = 'recommended_article',
  ReadMore = 'read_more',
  ReferAFriend = 'refer_a_friend',
  SearchButton = 'search_button',
  SettingsMenuItem = 'settings_menu_item',
  ShareButton = 'share_button',
  ShowMoreComments = 'show_more_comments',
  SideNavItem = 'side_nav_item',
  SideNavToggle = 'side_nav_toggle',
  SignIn = 'sign_in',
  SocialNavItem = 'social_nav_item',
  Subscribe = 'subscribe',
  SubscribeCBPT = 'subscribe_cbpt',
  SubscriberCouncilName = 'subscriber_council_name',
  TeamLeadCapture = 'team_lead_capture',
  Tech = 'tech',
  Twitter = 'twitter',
  Unfollow = 'unfollow',
  Upgrade = 'upgrade',
  ViewAllArticles = 'view_all_articles',
  ViewList = 'view_list',
  ViewMore = 'view_more',
  Weekend = 'weekend',
}

export enum TrackingLocation {
  AwarenessBar = 'awareness_bar',
  ArticlePickups = 'article_pickups',
  BottomBanner = 'bottom_banner',
  BriefingSubscriptionUnit = 'briefing_subscription_unit',
  CommentSection = 'comment_section',
  CommentReplySection = 'comment_reply_section',
  ContentBody = 'content_body',
  ContinuedReading = 'continued_reading',
  CommunityCommentsUnit = 'community_comments_unit',
  Footer = 'footer',
  ForumUnit = 'forum_unit',
  LatestNewsletterArticles = 'latest_newsletter_articles',
  LatestProArticles = 'latest_pro_articles',
  MainRiver = 'main_river',
  Modal = 'modal',
  MostPopularArticles = 'most_popular_articles',
  Nav = 'nav',
  PaymentForm = 'payment_form',
  Paywall = 'paywall',
  RecommendedArticles = 'recommended_articles',
  RelatedStories = 'related_stories',
  SideFeed = 'side_feed',
  ShareModal = 'share_modal',
  SignupForm = 'subscribe_form',
  SubscriberCouncil = 'subscriber_council',
  SubscribeToNewsletter = 'subscribe_to_newsletter',
  TopArticles = 'top_articles',
  TopArticlesSide = 'top_articles_side',
  UpcomingEvents = 'upcoming_events',
  WeekendArticles = 'weekend_articles',
}

export enum ImpressionElement {
  AwarenessBar = 'awareness_bar',
  BrandPartnershipsAd = 'brand_partnerships_ad',
  CorporateSubscriptionAd = 'corporate_subscription_ad',
  DeepLinkedComment = 'deep_linked_comment',
  Modal = 'modal',
  Payment = 'payment',
  PaywallBanner = 'paywall_banner',
  PaywallFull = 'paywall_full',
}

export enum TypingInput {
  Comment = 'comment',
  CorporateLeadCapture = 'corporate_lead_capture',
  Email = 'email',
  FullName = 'full_name',
  LeadCapture = 'lead_capture',
  Reply = 'reply',
  TeamLeadCapture = 'team_lead_capture',
}

// Main list of event properties that are all optional to satisfy
// sometimes not passing all properties at the same time with useEventTracking
export interface EventProperties {
  device?: string;
  eventName?: Event;
  page?: string;
  location?: string;
  currentUser?: User;
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  accessLevelId?: number;
  clickTarget?: string;
  contentType?: ContentType;
  contentId?: number;
  contentIndex?: number;
  creatorIds?: number[];
  impressionElement?: string;
  tiEventName?: string;
  tiEventLocation?: string;
}

// This needs to extend to all of the possible properties. We can't use EventProperties
// because we need to make sure that we have context and omit currentUser.
interface SegmentEventProperties extends EventProperties {
  context?: {
    identity_id?: number;
  };
}

interface ClickEventProperties {
  clickTarget: string;
  clickText?: string;
  clickLink?: string;
  contentId?: number;
  contentIndex?: number;
  contentType?: ContentType;
  creatorIds?: number[];
  offerCode?: string;
  tiEventName?: string;
  tiEventLocation?: string;
}

interface ImpressionProperties {
  impressionElement: string;
}

interface TypingProperties {
  inputTarget: TypingInput;
}

export function click(properties: ClickEventProperties) {
  return { ...properties, eventName: Event.Click };
}

export function impression(properties: ImpressionProperties) {
  return { ...properties, eventName: Event.Impression };
}

export function typing(properties: TypingProperties) {
  return { ...properties, eventName: Event.Typing };
}

export function fireEvent(properties: EventProperties) {
  const [segmentEventProperties, context] = cloneAndSetPropertiesForSegment(properties);
  global?.analytics?.track(properties.eventName, segmentEventProperties, { context });
}

const cloneAndSetPropertiesForSegment = (
  properties: EventProperties
): [SegmentEventProperties, { identity_id: number }] => {
  const segmentProperties: SegmentEventProperties = cloneDeep(properties);
  delete segmentProperties.currentUser;

  segmentProperties.device = deviceType;
  segmentProperties.subscriptionStatus = properties.currentUser?.subscription_status;
  segmentProperties.accessLevelId = properties.currentUser?.access_level;
  const context = { identity_id: properties.currentUser?.identity_id };
  return [segmentProperties, context];
};

export function trackSubscriptionInGaAndOptimizely({
  affiliation,
  email,
  initialSubscriptionPrice,
  offerCode,
  productSku,
  redirectTo,
  stripePlanName,
  subscriptionVariant,
  userId,
}: SubscribeUserResponse) {
  window.createAhoyEvent(email);
  analyticsIdentify(userId.toString(), { call_location: 'tracking.ts' });

  window['optimizely'] = window['optimizely'] || [];
  window['optimizely'].push({
    type: 'event',
    eventName: 'subscription',
    tags: {
      revenue: parseInt(initialSubscriptionPrice) * 100,
    },
  });

  const subscriptionGaEventProperties = {
    affiliation: affiliation,
    currency: 'USD',
    items: [
      {
        brand: 'The Information',
        category: 'Subscriptions',
        coupon: offerCode,
        id: productSku, // Aligns with GA SKU
        name: stripePlanName, // Aligns with GA Product Name
        price: initialSubscriptionPrice,
        quantity: 1,
        variant: subscriptionVariant,
      },
    ],
    transaction_id: `${userId}_${Date.now()}`,
    value: parseInt(initialSubscriptionPrice),
  };
  gtag('event', 'purchase', subscriptionGaEventProperties);

  window.location.href = redirectTo;
}

export function trackLeadInOptimizely({ userWasCreated }) {
  if (['/subscribe'].includes(window.location.pathname) && userWasCreated) {
    window['optimizely'] = window['optimizely'] || [];
    window['optimizely'].push({
      type: 'event',
      eventName: 'subscribe-page-lead',
      tags: {
        revenue: 0, // Optional in cents as integer (500 == $5.00)
        value: 0.0, // Optional as float
      },
    });
  }
}

export function trackLeadInGa(leadType: string, leadDetails: LeadDetails) {
  window.dataLayer = window.dataLayer || [];
  const gaLeadEventProperties = {
    event: LEAD_CREATED_EVENT,
    eventModel: {
      leadType: leadType,
      ...leadDetails,
    },
  };
  window.dataLayer.push(gaLeadEventProperties);
}

export function transformUnitNameToLocation(unitName: string) {
  return `${transformToSnakeCase(unitName)}_unit`;
}
